import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { GuideTourStep } from "./types";
import type { UseGuideTourOptions } from "./types";

function readStorage(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeStorage(key: string) {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    /* ignore */
  }
}

export function useGuideTour({
  steps,
  storageKey,
  flowStepIndex,
  persistCompletion = true,
  onEscape,
}: UseGuideTourOptions) {
  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const prevFlowRef = useRef(flowStepIndex);
  const flowStepRef = useRef(flowStepIndex);
  flowStepRef.current = flowStepIndex;

  const activeStep: GuideTourStep | null = useMemo(
    () => (stepIndex >= 0 && stepIndex < steps.length ? steps[stepIndex] : null),
    [stepIndex, steps]
  );

  const markDone = useCallback(() => {
    writeStorage(storageKey);
  }, [storageKey]);

  const stop = useCallback(() => {
    setIsActive(false);
  }, []);

  const complete = useCallback(() => {
    if (persistCompletion) markDone();
    stop();
  }, [markDone, persistCompletion, stop]);

  const skip = useCallback(() => {
    if (persistCompletion) markDone();
    stop();
  }, [markDone, persistCompletion, stop]);

  const start = useCallback(() => {
    if (persistCompletion && readStorage(storageKey)) return;
    prevFlowRef.current = flowStepRef.current;
    setStepIndex(0);
    setIsActive(true);
  }, [persistCompletion, storageKey]);

  useEffect(() => {
    if (!isActive) {
      prevFlowRef.current = flowStepIndex;
      return;
    }
    const step = steps[stepIndex];
    const threshold = step?.advanceWhenFlowIndex;
    if (
      step?.action === "clickNext" &&
      threshold != null &&
      flowStepIndex >= threshold &&
      prevFlowRef.current < threshold
    ) {
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    }
    prevFlowRef.current = flowStepIndex;
  }, [flowStepIndex, isActive, stepIndex, steps]);

  useLayoutEffect(() => {
    if (!isActive || !activeStep) {
      setTargetRect(null);
      return;
    }

    const el = document.querySelector(activeStep.selector);
    if (!(el instanceof Element)) {
      setTargetRect(null);
      return;
    }

    const measure = () => {
      const r = el.getBoundingClientRect();
      setTargetRect(new DOMRect(r.left, r.top, r.width, r.height));
    };

    measure();
    el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });

    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);

    const id = window.requestAnimationFrame(measure);
    return () => {
      window.cancelAnimationFrame(id);
      ro.disconnect();
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [activeStep, isActive]);

  const goNext = useCallback(() => {
    const step = steps[stepIndex];
    if (!step) return;

    if (step.action === "clickNext") {
      const el = document.querySelector(step.selector);
      if (el instanceof HTMLElement) el.click();
      return;
    }

    if (stepIndex >= steps.length - 1) {
      complete();
      return;
    }
    setStepIndex((i) => i + 1);
  }, [complete, stepIndex, steps]);

  const goPrev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (onEscape) onEscape();
      else skip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isActive, onEscape, skip]);

  const jumpToStep = useCallback((n: number) => {
    setStepIndex(Math.max(0, Math.min(n, steps.length - 1)));
  }, [steps.length]);

  return {
    isActive,
    stepIndex,
    activeStep,
    targetRect,
    totalSteps: steps.length,
    start,
    stop,
    skip,
    complete,
    goNext,
    goPrev,
    jumpToStep,
    isCompleted: () => readStorage(storageKey),
  };
}
